import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"

import type { LotCorrectionAdjustmentFormValues } from "../../schemas"
import type { AdjustmentSelectedLot } from "../../types"

import { LotCorrectionAdjustmentExistingLotRow } from "./LotCorrectionAdjustmentExistingLotRow"
import { LotCorrectionAdjustmentNewLotRow } from "./LotCorrectionAdjustmentNewLotRow"

import { Button } from "@/components/common/Button"
import type { Product } from "@/graphql/codegen/graphql"

const COLUMN_COUNT = 12

interface LotCorrectionAdjustmentFormLotsProps {
  selectedLots: AdjustmentSelectedLot[]
  product: Product
  uomName: string
  existingLotFields: FieldArrayWithId<LotCorrectionAdjustmentFormValues, "existingLots">[]
  newLotFields: FieldArrayWithId<LotCorrectionAdjustmentFormValues, "newLots">[]
  duplicateLotIndexes: { existingLots: Set<number>; newLots: Set<number> }
  onLotAdd: () => void
  onLotRemove: (lotIndex: number) => void
}

export const LotCorrectionAdjustmentFormLots: FC<LotCorrectionAdjustmentFormLotsProps> = ({
  selectedLots,
  product,
  uomName,
  existingLotFields,
  newLotFields,
  duplicateLotIndexes,
  onLotAdd,
  onLotRemove,
}) => {
  const hasSelection = existingLotFields.length > 0
  const selectedPallet = selectedLots.length ? selectedLots[0].pallet : null

  return (
    <Box className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Box className='mb-4 flex items-center justify-between'>
        <Typography variant='h6' className='font-bold leading-10'>
          2. Adjust Lot Contents
        </Typography>
        {hasSelection && (
          <Button
            appearance='text'
            startIcon={<AddOutlined />}
            onClick={onLotAdd}
            data-testid='inventory.lot-correction-adjustment-form-lots.add-lot-button'
          >
            Add Lot
          </Button>
        )}
      </Box>
      <TableContainer className='min-h-0 flex-1 overflow-auto'>
        <Table stickyHeader className='w-full table-auto'>
          <TableHead>
            <TableRow>
              <TableCell className='whitespace-nowrap'>Reference #</TableCell>
              <TableCell className='whitespace-nowrap'>Location</TableCell>
              <TableCell className='w-56 min-w-56 max-w-56 whitespace-nowrap'>Lot #</TableCell>
              <TableCell className='w-56 min-w-56 max-w-56 whitespace-nowrap'>Batch #</TableCell>
              <TableCell className='whitespace-nowrap'>Remaining Shelf Life</TableCell>
              <TableCell className='whitespace-nowrap'>Packaging</TableCell>
              <TableCell className='whitespace-nowrap'>Units</TableCell>
              <TableCell className='whitespace-nowrap'>Pkg UoM</TableCell>
              <TableCell className='whitespace-nowrap'>Current Lot UoM</TableCell>
              <TableCell className='whitespace-nowrap'>New UoM Qty</TableCell>
              <TableCell className='min-w-36 whitespace-nowrap'></TableCell>
              <TableCell className='w-12 min-w-12' />
            </TableRow>
          </TableHead>

          <TableBody>
            {!hasSelection && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className='py-8'>
                  <p className='flex flex-col items-center gap-y-2 text-center'>
                    <span className='font-semibold text-gray-700'>No pallet selected</span>
                    <span className='max-w-[340px]'>
                      Select a single pallet from the table above to rename lot/batch or adjust quantities.
                    </span>
                  </p>
                </TableCell>
              </TableRow>
            )}

            {hasSelection &&
              existingLotFields.map((field, index) => {
                const lot = selectedLots[index]

                if (!lot) return null

                return (
                  <LotCorrectionAdjustmentExistingLotRow
                    key={field.id}
                    product={product}
                    uomName={uomName}
                    index={index}
                    lot={lot}
                    isDuplicate={duplicateLotIndexes.existingLots.has(index)}
                  />
                )
              })}

            {hasSelection &&
              newLotFields.map((newLotField, index) => (
                <LotCorrectionAdjustmentNewLotRow
                  key={newLotField.id}
                  product={product}
                  uomName={uomName}
                  index={index}
                  pallet={selectedPallet}
                  isDuplicate={duplicateLotIndexes.newLots.has(index)}
                  onRemove={() => onLotRemove(index)}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
